// src/layouts/DashboardLayout.js
import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation ,useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AppBarStyled,
  FlexContainer,
  MainBox,
  ProfileContainer,
  ProfileName,
  ProfileTitle,
  ToolbarStyled,
} from "../components/DashboardComponents";
import {
  Toolbar,
  IconButton,
  Avatar,
  Box,
  CssBaseline,
  Tooltip,
  useMediaQuery,
  useTheme,
  Button,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AddIcon from "@mui/icons-material/Add";
import logoImage from "../assets/new_logo.png";
import profileImage from "../assets/profile.png";
import { getUserById } from "../services/userService";
import ResponsiveDrawer from "../components/DrawerComponent";
import DemoFab from "../components/ui/FabButton";
import EditUserForm from "../pages/Dashboard/Users/EditUserForm";
import { paths } from "../routes/paths";
import Intercom from "@intercom/messenger-js-sdk";
import { selectJob } from "../../src/store/reducers/appreducer";
import JobsDock from "../components/JobsDock";

const Root = styled("div")({
  display: "flex",
});

const MenuButton = styled(IconButton)(({ theme }) => ({
  marginRight: "16px",
}));

const CreateJobButton = styled(Button)({
  backgroundColor: "#4F75FF",
  color: "white",
  borderRadius: "6px",
  padding: "8px 12px",
  textTransform: "none",
  fontFamily: "Raleway",
  fontWeight: 600,
  fontSize: "12px",
  "&:hover": {
    backgroundColor: "#3D5FCC",
  },
});

const ActionIconsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  marginRight: "12px",
  [theme.breakpoints.down('md')]: {
    gap: '3px',
    marginRight: '6px'
  }
}));

const DashboardLayout = () => {
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(true); // Drawer open by default
  const userId = useSelector((state) => state.appreducer.account.userId);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { jobId } = useParams();

  const isJobManagementPage = location.pathname.includes('/jobs');

  const handleCreateJob = () => {
    navigate(paths.dashboard.jobs + '?create=true');
  };

  useEffect(() => {
    if (userId) {
      const fetchUserDetails = async () => {
        try {
          const userData = await getUserById(userId);
          setUserDetails(userData);
        } catch (err) {
          console.error("Error fetching user details:", err);
        }
      };
      fetchUserDetails();
    }
  }, [userId]);

  useEffect(() => {
    if (userDetails) {
      Intercom("update", {
        app_id: "r4y3y6fs",
        user_id: userDetails.userId,
        name: `${userDetails.firstName} ${userDetails.lastName}`,
        email: userDetails.email,
        created_at: Math.floor(Date.now() / 1000),
      });
    }
  }, [userDetails]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleListItemClick = (event, path) => {
    if (path === "logout") {
      handleLogout();
      return;
    }
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("is_admin");
    sessionStorage.clear();
    dispatch(selectJob(null));
    navigate(paths.auth.signin, { replace: true });
  };

  const handleEditProfile = () => {
    setEditFormOpen(true);
  };

  const handleCloseEditForm = () => {
    setEditFormOpen(false);
  };

  const updateUserDetails = (updatedData) => {
    setUserDetails((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  return (
    <Root>
      <CssBaseline />
      <AppBarStyled
        position="fixed"
        color="default"
        open={drawerOpen && !isMobile}
        sx={{
          width: {
            xs: "100%",
            md: drawerOpen ? "calc(100% - 245px)" : "100%",
          },
          ml: {
            xs: 0,
            md: drawerOpen ? "245px" : 0,
          },
          transition:
            "width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, margin-left 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
        }}
      >
        <ToolbarStyled>
          {/* Left Section - Menu Button */}
          {!isMobile && (
            <MenuButton
              color="inherit"
              aria-label="toggle drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </MenuButton>
          )}

          {/* Right Section - Actions and Profile */}
          <Box sx={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
            {isJobManagementPage && (
              <CreateJobButton
                startIcon={<AddIcon />}
                onClick={handleCreateJob}
                sx={{ mr: 4 }}
              >
                CREATE JOB
              </CreateJobButton>
            )}
{/*             
            <ActionIconsContainer>
              <Tooltip title="Settings">
                <IconButton color="inherit" size="small" sx={{ p: 0.5 }}>
                  <SettingsIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </ActionIconsContainer> */}

            <ProfileContainer>
              <Tooltip title="Profile options">
                <IconButton color="inherit" onClick={handleEditProfile} sx={{ p: 0.5 }}>
                  <Avatar
                    alt="Profile"
                    src={profileImage}
                    sx={{ width: 36, height: 36 }}
                  />
                </IconButton>
              </Tooltip>
              <Box display="flex" flexDirection="column">
                <ProfileName variant="body1" noWrap>
                  {userDetails
                    ? `${userDetails.firstName} ${userDetails.lastName}`
                    : "Loading..."}
                </ProfileName>
                <ProfileTitle variant="body2" noWrap>
                  HR Manager
                </ProfileTitle>
              </Box>
            </ProfileContainer>
          </Box>
        </ToolbarStyled>
      </AppBarStyled>

      <ResponsiveDrawer
        navigate={navigate}
        logoImage={logoImage}
        location={location}
        handleListItemClick={handleListItemClick}
        drawerOpen={drawerOpen}
        handleDrawerToggle={handleDrawerToggle}
      />

      <MainBox
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          overflow: "hidden",
          border: "none",
          marginLeft: { xs: 0, sm: 0 },
          width: {
            xs: "100%",
            sm: drawerOpen ? "calc(100% - 245px)" : "100%",
          },
          transition:
            "width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, margin 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
        }}
      >
        <Toolbar />
        {jobId && <JobsDock jobId={jobId} />}
        <Outlet />
        <DemoFab />
      </MainBox>

      <EditUserForm
        open={editFormOpen}
        onClose={handleCloseEditForm}
        user={userDetails}
        onSave={handleCloseEditForm}
        onCancel={handleCloseEditForm}
        updateUserDetails={updateUserDetails}
      />
    </Root>
  );
};

export default DashboardLayout;
